import React from "react";
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";

import { doFetchJson } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/**
 * BatchFromZoneModal — generar un lote de entradas numeradas a partir
 * de una zona de sala (una entrada por butaca habilitada).
 *
 * Props:
 *  - show: boolean
 *  - idVenue: integer — sala asignada a la función
 *  - idRepresentacionFecha: integer
 *  - onClose: () => void
 *  - onCreated: (batch) => void
 */
class BatchFromZoneModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      zones: [],
      loadingZones: false,
      selectedZone: null,
      name: "",
      price: "",
      saving: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState({ selectedZone: null, name: "", price: "", saving: false });
      this.loadZones();
    }
  }

  loadZones = () => {
    const { idVenue } = this.props;
    if (!idVenue) return;
    this.setState({ loadingZones: true, zones: [] });
    doFetchJson("TEAN", "getVenue/" + idVenue)
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          const zones = (json.DATA.zones || [])
            .filter((z) => !z.isStage && z.numbered)
            .map((z) => ({
              idZone: z.idZone,
              name: z.name,
              color: z.color,
              seatCount: (z.seats || []).filter((s) => s.enabled).length,
              numberingScheme: z.numberingScheme,
            }));
          this.setState({ zones, loadingZones: false });
        } else {
          toast.show({ message: "No se pudo cargar la sala", type: "danger" });
          this.setState({ loadingZones: false });
        }
      })
      .catch(() => {
        toast.show({ message: "Error de red", type: "danger" });
        this.setState({ loadingZones: false });
      });
  };

  handleClose = () => this.props.onClose && this.props.onClose();

  selectZone = (zone) => {
    this.setState((prev) => ({
      selectedZone: prev.selectedZone && prev.selectedZone.idZone === zone.idZone ? null : zone,
    }));
  };

  handleSave = () => {
    const { idRepresentacionFecha, onCreated } = this.props;
    const { selectedZone, name, price } = this.state;

    if (!selectedZone) {
      toast.show({ message: "Selecciona una zona", type: "warning" });
      return;
    }
    if (!name.trim()) {
      toast.show({ message: "Introduce un nombre para el lote", type: "warning" });
      return;
    }

    const body = {
      idRepresentacionFecha,
      idZone: selectedZone.idZone,
      name: name.trim(),
      price: price !== "" ? parseFloat(price) : null,
    };

    this.setState({ saving: true });
    doFetchJson("TEAN", "generateBatchFromZone", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((json) => {
        if (json && json.STATE === "SUCCESS") {
          toast.show({
            message: `Lote creado con ${selectedZone.seatCount} entradas`,
            type: "success",
          });
          if (onCreated) onCreated(json.DATA);
          this.handleClose();
        } else {
          toast.show({ message: (json && json.MESSAGE) || "No se pudo generar el lote", type: "danger" });
        }
      })
      .catch(() => toast.show({ message: "Error de red", type: "danger" }))
      .finally(() => this.setState({ saving: false }));
  };

  render() {
    const { show } = this.props;
    const { zones, loadingZones, selectedZone, name, price, saving } = this.state;

    return (
      <MDBModal show={show} onHide={this.handleClose} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                <MDBIcon fas icon="layer-group" className="me-2" />
                Generar lote desde zona
              </MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={this.handleClose} />
            </MDBModalHeader>
            <MDBModalBody>
              <p className="text-muted small mb-3">
                Se creará una entrada por cada butaca habilitada en la zona seleccionada.
                Los números de entrada coincidirán con los identificadores de butaca.
              </p>

              <div className="mb-3">
                <label className="form-label fw-semibold">Zona de sala</label>
                {loadingZones ? (
                  <div className="text-center py-2">
                    <MDBSpinner size="sm" />
                  </div>
                ) : zones.length === 0 ? (
                  <div className="text-muted small">
                    No hay zonas numeradas disponibles en esta sala.
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {zones.map((z) => (
                      <button
                        key={z.idZone}
                        type="button"
                        onClick={() => this.selectZone(z)}
                        className="btn btn-sm"
                        style={{
                          backgroundColor: selectedZone && selectedZone.idZone === z.idZone
                            ? z.color || "#0d6efd"
                            : "transparent",
                          borderColor: z.color || "#0d6efd",
                          color: selectedZone && selectedZone.idZone === z.idZone
                            ? "#fff"
                            : z.color || "#0d6efd",
                          borderWidth: 2,
                        }}
                      >
                        {z.name}
                        <span className="ms-1 badge bg-white text-dark" style={{ fontSize: "0.7rem" }}>
                          {z.seatCount} butacas
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Nombre del lote *</label>
                <input
                  className="form-control"
                  value={name}
                  onChange={(e) => this.setState({ name: e.target.value })}
                  placeholder="Ej: Zona A — General"
                  maxLength={100}
                />
              </div>

              <div className="mb-1">
                <label className="form-label fw-semibold">Precio por entrada (€)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  value={price}
                  onChange={(e) => this.setState({ price: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" outline onClick={this.handleClose} disabled={saving}>
                Cancelar
              </MDBBtn>
              <MDBBtn color="primary" onClick={this.handleSave} disabled={saving || !selectedZone || !name.trim()}>
                {saving ? (
                  <><MDBSpinner size="sm" className="me-2" />Generando...</>
                ) : (
                  <><MDBIcon fas icon="magic" className="me-1" />Generar lote</>
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    );
  }
}

export default BatchFromZoneModal;
